import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoginModal from '../components/LoginModal';
import '../styles/pages/MarketerLandingPage.css';

const API_URL = process.env.REACT_APP_API_URL;

function MarketerLandingPage() {
  const navigate = useNavigate();
  const [loginOpen, setLoginOpen] = useState(false);
  const rootRef = useRef(null);
  const [me, setMe] = useState(null); // 로그인 사용자 { role } | null

  // 로그인 상태 확인 (LP는 리다이렉트하지 않고 버튼 동작만 전환)
  useEffect(() => {
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 3000);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/user/me`, { credentials: 'include', signal: ac.signal });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.ok) setMe({ role: data.role });
      } catch {
        /* 비로그인/네트워크 — 기본(로그아웃) 뷰 유지 */
      } finally {
        clearTimeout(timer);
      }
    })();
    return () => { clearTimeout(timer); ac.abort(); };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const nav = root.querySelector('#nav');
    const onScroll = () => {
      if (nav) nav.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    root.querySelectorAll('.reveal').forEach((el) => io.observe(el));

    return () => {
      window.removeEventListener('scroll', onScroll);
      io.disconnect();
    };
  }, []);

  // 로그인 상태면(역할 무관): 로그인 버튼 숨김 + 시작 CTA → 본인 역할 마이비즈
  const isLoggedIn = me?.role === 'marketer' || me?.role === 'influencer';
  const startTo = isLoggedIn ? `/${me.role}/home` : '/marketer/register';

  return (
    <div className="lp-mkt" ref={rootRef}>
      <header className="nav" id="nav">
        <div className="wrap nav__in">
          {/* 상단 내비는 폭 제약으로 병기 제외 — 로그인 버튼만 병기 유지 (본문 히어로·CTA는 병기) */}
          <div className="brand" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}><span className="brand__dot"></span>셀프애드<span className="brand__tag">광고주</span></div>
          <nav className="nav__links">
            <a href="#how">작동 방식</a>
            <a href="#feature">기능</a>
            <a href="#agency">광고대행</a>
            <a href="#faq">FAQ</a>
          </nav>
          <div className="nav__spacer"></div>
          {!isLoggedIn && <button className="btn btn--sm btn--ghost" onClick={() => setLoginOpen(true)}>로그인 (Log in)</button>}
          <button className="btn btn--sm btn--primary" onClick={() => navigate(startTo)}>광고주로 시작하기</button>
        </div>
      </header>

      {/* HERO */}
      <section className="hero">
        <div className="blob blob--1"></div>
        <div className="blob blob--2"></div>
        <div className="wrap hero__grid">
          <div className="hero__copy">
            <span className="eyebrow reveal"><b></b>광고주를 위한 셀프 마케팅 (Self-serve marketing for advertisers)</span>
            <h1 className="hero__title reveal" data-d="1">인플루언서 마케팅,<br /><span className="accent">직접 만들고</span> 운영하세요<span style={{ display: 'block', fontSize: '0.42em', fontWeight: 600, color: '#6b7686', marginTop: '10px' }}>(Create and run influencer campaigns yourself)</span></h1>
            <p className="hero__sub reveal" data-d="2">대행사 미팅도 긴 견적도 필요 없습니다. 원하는 조건으로 프로젝트를 만들면 검증된 나노·마이크로 인플루언서가 직접 지원해요. <span style={{ color: '#8b95a5' }}>(No agency needed — create a project and verified influencers apply directly.)</span></p>
            <div className="hero__cta reveal" data-d="3">
              <button className="btn btn--lg btn--primary" onClick={() => navigate(startTo)}>광고주로 시작하기 (Start as Advertiser)</button>
              <button className="btn btn--lg btn--ghost" onClick={() => rootRef.current?.querySelector('#how')?.scrollIntoView({ behavior: 'smooth' })}>작동 방식 보기 (How it works)</button>
            </div>
            <p className="hero__note reveal" data-d="3">가입 무료 · 카드 등록 없이 둘러보기 (Free to join · no card required)</p>
          </div>
          <div className="hero__media reveal" data-d="2">
            <div className="browser">
              <div className="browser__bar">
                <span className="dot r"></span><span className="dot y"></span><span className="dot g"></span>
                <span className="browser__url">app.selfad.co.kr/marketer/projects</span>
              </div>
              <div className="browser__body">
                <div className="mk-head"><div className="mk-title">신상 코스메틱 체험단 (Cosmetics review)</div><span className="pill pill--blue">모집 중 (Recruiting)</span></div>
                <div className="mk-grid">
                  <div className="mk-card"><div className="mk-card__top"><div className="mk-card__ic" style={{ background: '#2f6bff' }}>₩</div><div><div className="mk-card__t">예산 (Budget)</div><div className="mk-card__s">포인트 집행</div></div></div><div className="mk-num">1,500,000P</div></div>
                  <div className="mk-card"><div className="mk-card__top"><div className="mk-card__ic" style={{ background: '#0fb37a' }}>≡</div><div><div className="mk-card__t">모집 인원 (Headcount)</div><div className="mk-card__s">인플루언서</div></div></div><div className="mk-num">15명</div></div>
                  <div className="mk-card"><div className="mk-card__top"><div className="mk-card__ic" style={{ background: '#8b5cf6' }}>★</div><div><div className="mk-card__t">채널 (Channels)</div><div className="mk-card__s">플랫폼</div></div></div><div className="mk-num">IG · YT</div></div>
                  <div className="mk-card"><div className="mk-card__top"><div className="mk-card__ic" style={{ background: '#f59e0b' }}>⏱</div><div><div className="mk-card__t">기간 (Duration)</div><div className="mk-card__s">모집~검수</div></div></div><div className="mk-num">14일</div></div>
                </div>
                <div className="mk-foot">
                  <div className="avatar-stack"><span style={{ background: '#2f6bff' }}>K</span><span style={{ background: '#0fb37a' }}>J</span><span style={{ background: '#8b5cf6' }}>M</span><span style={{ background: '#f59e0b' }}>S</span><span style={{ background: '#0b1324' }}>+9</span></div>
                  <span className="mk-card__s">지원자 13명 · 매칭 대기 중 (13 applicants · matching)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FACTS */}
      <div className="wrap">
        <div className="stats reveal">
          <div className="stats__grid">
            <div><div className="stat__num">5,000<span className="u">원~</span></div><div className="stat__label">인플루언서 3명당 수수료</div></div>
            <div><div className="stat__num">포인트</div><div className="stat__label">예산 직접 집행</div></div>
            <div><div className="stat__num">검수 후</div><div className="stat__label">결과물 확인·지급</div></div>
            <div><div className="stat__num">자동 환원</div><div className="stat__label">미사용 보증금</div></div>
          </div>
        </div>
      </div>

      {/* FEATURE 1: 프로젝트 만들기 */}
      <section className="block" id="feature">
        <div className="wrap feat">
          <div className="feat__copy reveal">
            <div className="kicker kicker--blue">STEP 01 · 프로젝트 만들기</div>
            <h2 className="h2">원하는 조건으로<br />프로젝트를 등록하세요</h2>
            <p className="lead">예산, 모집 인원, 채널, 기간만 정하면 끝. 몇 분이면 첫 프로젝트를 게시할 수 있습니다.</p>
            <ul className="feat__list">
              <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M2 12h20"/></svg></span><div><div className="feat__li-t">포인트로 간편하게 집행</div><div className="feat__li-d">충전한 포인트로 예산을 직접 운용하고, 집행 내역을 실시간으로 확인하세요.</div></div></li>
              <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3h18v14H3zM3 9h18M8 21h8"/></svg></span><div><div className="feat__li-t">채널·인원·기간 자유 설정</div><div className="feat__li-d">인스타그램·유튜브·블로그·틱톡 중 원하는 채널과 규모를 직접 정합니다.</div></div></li>
            </ul>
            <button className="btn btn--lg btn--primary" onClick={() => navigate(startTo)}>프로젝트 만들기 (Create Project)</button>
          </div>
          <div className="feat__media reveal" data-d="1">
            <div className="browser">
              <div className="browser__bar"><span className="dot r"></span><span className="dot y"></span><span className="dot g"></span><span className="browser__url">app.selfad.co.kr/marketer/projects/new</span></div>
              <div className="browser__body">
                <div className="mk-head"><div className="mk-title">새 프로젝트</div><span className="pill pill--blue">작성 중</span></div>
                <div className="mk-grid">
                  <div className="mk-card"><div className="mk-card__top"><div className="mk-card__ic" style={{ background: '#2f6bff' }}>₩</div><div><div className="mk-card__t">예산</div><div className="mk-card__s">포인트</div></div></div><div className="mk-num">1,500,000P</div></div>
                  <div className="mk-card"><div className="mk-card__top"><div className="mk-card__ic" style={{ background: '#0fb37a' }}>≡</div><div><div className="mk-card__t">모집</div><div className="mk-card__s">인플루언서</div></div></div><div className="mk-num">15명</div></div>
                  <div className="mk-card"><div className="mk-card__top"><div className="mk-card__ic" style={{ background: '#8b5cf6' }}>★</div><div><div className="mk-card__t">채널</div><div className="mk-card__s">플랫폼</div></div></div><div className="mk-num">IG · YT</div></div>
                  <div className="mk-card"><div className="mk-card__top"><div className="mk-card__ic" style={{ background: '#f59e0b' }}>⏱</div><div><div className="mk-card__t">기간</div><div className="mk-card__s">모집~검수</div></div></div><div className="mk-num">14일</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE 2: 인플루언서 매칭 */}
      <section className="block" style={{ background: 'var(--bg-soft)', borderRadius: '40px' }}>
        <div className="wrap feat feat--rev">
          <div className="feat__copy reveal">
            <div className="kicker kicker--blue">STEP 02 · 인플루언서 매칭</div>
            <h2 className="h2">어울리는 인플루언서를<br />직접 만나세요</h2>
            <p className="lead">프로젝트를 게시하면 관심 있는 인플루언서가 지원합니다. 프로필과 채널을 확인하고 원하는 크리에이터를 선택하세요.</p>
            <ul className="feat__list">
              <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8"/></svg></span><div><div className="feat__li-t">지원자 프로필로 비교·선택</div><div className="feat__li-d">채널·분야·활동 이력을 보고 프로젝트에 맞는 인플루언서를 직접 고릅니다.</div></div></li>
              <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span><div><div className="feat__li-t">선택한 만큼만 집행</div><div className="feat__li-d">필요한 인원만 승인해 예산을 낭비 없이 운용할 수 있습니다.</div></div></li>
            </ul>
          </div>
          <div className="feat__media reveal" data-d="1">
            <div className="browser">
              <div className="browser__bar"><span className="dot r"></span><span className="dot y"></span><span className="dot g"></span><span className="browser__url">app.selfad.co.kr/marketer/applicants</span></div>
              <div className="browser__body">
                <div className="mk-head"><div className="mk-title">지원자 13명</div><span className="pill pill--blue">매칭 대기</span></div>
                <div className="mk-card" style={{ marginBottom: '10px' }}><div className="mk-card__top" style={{ margin: '0' }}><div className="mk-card__ic" style={{ background: '#2f6bff' }}>K</div><div style={{ flex: '1' }}><div className="mk-card__t">뷰티 크리에이터 · 김OO</div><div className="mk-card__s">인스타그램 · 릴스 · 팔로워 8.2K</div></div><span className="pill pill--blue">승인</span></div></div>
                <div className="mk-card" style={{ marginBottom: '10px' }}><div className="mk-card__top" style={{ margin: '0' }}><div className="mk-card__ic" style={{ background: '#0fb37a' }}>J</div><div style={{ flex: '1' }}><div className="mk-card__t">데일리 브이로그 · 정OO</div><div className="mk-card__s">유튜브 · 쇼츠 · 구독 12K</div></div><span className="pill pill--blue">승인</span></div></div>
                <div className="mk-card"><div className="mk-card__top" style={{ margin: '0' }}><div className="mk-card__ic" style={{ background: '#8b5cf6' }}>M</div><div style={{ flex: '1' }}><div className="mk-card__t">푸드 블로거 · 문OO</div><div className="mk-card__s">네이버 블로그 · 일 방문 3K</div></div><span className="pill" style={{ background: '#eef1f6', color: '#6b7686' }}>검토</span></div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE 3: 검수·정산 */}
      <section className="block">
        <div className="wrap feat">
          <div className="feat__copy reveal">
            <div className="kicker kicker--blue">STEP 03 · 검수 & 정산</div>
            <h2 className="h2">결과물을 검수하고,<br />투명하게 정산하세요</h2>
            <p className="lead">제출된 콘텐츠를 확인해 승인하면 보상이 지급됩니다. 집행부터 결산까지 한 화면에서 정리돼요.</p>
            <ul className="feat__list">
              <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span><div><div className="feat__li-t">결과물 검수 후 승인</div><div className="feat__li-d">요청사항 미충족 시 반려하면 셀프애드팀이 객관적으로 재검토합니다. (7일간 미검수 시 자동 승인)</div></div></li>
              <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18M7 14l4-4 3 3 5-6"/></svg></span><div><div className="feat__li-t">투명한 정산·결산 리포트</div><div className="feat__li-d">프로젝트별 성과와 비용을 명확하게 정리해 드립니다.</div></div></li>
            </ul>
            <button className="btn btn--lg btn--primary" onClick={() => navigate(startTo)}>광고주로 시작하기 (Start as Advertiser)</button>
          </div>
          <div className="feat__media reveal" data-d="1">
            <div className="browser">
              <div className="browser__bar"><span className="dot r"></span><span className="dot y"></span><span className="dot g"></span><span className="browser__url">app.selfad.co.kr/marketer/settlement</span></div>
              <div className="browser__body">
                <div className="mk-head"><div className="mk-title">정산·결산</div><span className="pill pill--blue">완료</span></div>
                <div className="mk-grid">
                  <div className="mk-card"><div className="mk-card__top"><div className="mk-card__ic" style={{ background: '#2f6bff' }}>₩</div><div><div className="mk-card__t">총 집행</div><div className="mk-card__s">이번 프로젝트</div></div></div><div className="mk-num">1,320,000P</div></div>
                  <div className="mk-card"><div className="mk-card__top"><div className="mk-card__ic" style={{ background: '#0fb37a' }}>✓</div><div><div className="mk-card__t">승인 콘텐츠</div><div className="mk-card__s">검수 완료</div></div></div><div className="mk-num">11건</div></div>
                  <div className="mk-card"><div className="mk-card__top"><div className="mk-card__ic" style={{ background: '#8b5cf6' }}>↺</div><div><div className="mk-card__t">잔여 포인트</div><div className="mk-card__s">환급 가능</div></div></div><div className="mk-num">180,000P</div></div>
                  <div className="mk-card"><div className="mk-card__top"><div className="mk-card__ic" style={{ background: '#f59e0b' }}>%</div><div><div className="mk-card__t">평균 도달</div><div className="mk-card__s">채널 합산</div></div></div><div className="mk-num">리포트</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROJECT TYPES */}
      <section className="block">
        <div className="wrap">
          <div className="reveal">
            <div className="kicker kicker--blue">광고 프로젝트 4가지 유형</div>
            <h2 className="h2">내 사업에 맞는<br />프로젝트를 선택하세요</h2>
            <p className="lead">제품·서비스 형태에 따라 4가지 방식 중에서 고를 수 있어요. 유형마다 광고주가 준비할 것이 달라집니다.</p>
          </div>
          <div className="types">
            <div className="type reveal" data-d="1">
              <div className="type__ico">📦</div>
              <div className="type__t">제품 제공</div>
              <div className="type__eng">delivery</div>
              <div className="type__row"><b>추천 사업자</b>자체 재고 보유 브랜드·제조사</div>
              <div className="type__row"><b>광고주가 하는 것</b>제품을 인플루언서에게 직접 배송</div>
              <div className="type__row"><b>대표 사례</b>신상 캠핑체어, 화장품, 가전</div>
              <div className="type__cps"><span>CPS 가능</span></div>
            </div>
            <div className="type reveal" data-d="1">
              <div className="type__ico">🛒</div>
              <div className="type__t">제품 미제공</div>
              <div className="type__eng">purchase</div>
              <div className="type__row"><b>추천 사업자</b>온라인 판매자, 스마트스토어</div>
              <div className="type__row"><b>광고주가 하는 것</b>제품 정보 + 구매처 URL 제공</div>
              <div className="type__row"><b>대표 사례</b>의류 브랜드, 식품</div>
              <div className="type__cps"><span>CPS 가능</span></div>
            </div>
            <div className="type reveal" data-d="2">
              <div className="type__ico">📍</div>
              <div className="type__t">장소 방문</div>
              <div className="type__eng">visit</div>
              <div className="type__row"><b>추천 사업자</b>카페·식당·매장·체험 공간</div>
              <div className="type__row"><b>광고주가 하는 것</b>매장 위치 + 방문 안내 제공</div>
              <div className="type__row"><b>대표 사례</b>카페 신메뉴, 헬스장, 미용실</div>
              <div className="type__cps"><span style={{ background: '#eef1f6', color: '#6b7686' }}>CPS 미사용</span></div>
            </div>
            <div className="type reveal" data-d="2">
              <div className="type__ico">🎨</div>
              <div className="type__t">자율 형식</div>
              <div className="type__eng">free</div>
              <div className="type__row"><b>추천 사업자</b>광고 형식 자유 (무형 서비스)</div>
              <div className="type__row"><b>광고주가 하는 것</b>광고 형식·제품/장소 자율 정의</div>
              <div className="type__row"><b>대표 사례</b>게임 신작, 강의, 행사</div>
              <div className="type__cps"><span style={{ background: '#fff5e6', color: '#b4700f' }}>CPS 케이스별</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* CPS */}
      <section className="block">
        <div className="wrap">
          <div className="cps reveal">
            <div className="cps__grid">
              <div>
                <div className="kicker">CPS · 성과 기반 리워드</div>
                <h2 className="h2">쓴 만큼만,<br />성과 난 만큼만 나갑니다</h2>
                <p className="lead">CPS(Cost Per Sale)는 인플루언서가 유입시킨 실제 구매 고객 1명당 추가 리워드를 지급하는 방식이에요. 설정한 보증금 안에서만 차감되고, 성과가 낮아도 미사용 보증금은 전액 자동 환원됩니다.</p>
                <ul className="feat__list">
                  <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span><div><div className="feat__li-t">ROI를 숫자로 확인</div><div className="feat__li-d">실제 매출로 이어진 유입만큼만 성과 리워드가 지급돼요. (스마트스토어·자사몰·쿠팡 파트너스 추적)</div></div></li>
                  <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span><div><div className="feat__li-t">손실 위험 0</div><div className="feat__li-d">모든 회차 결산 후 미사용 보증금은 광고주 포인트로 자동 환원됩니다.</div></div></li>
                </ul>
              </div>
              <div className="cps__ex">
                <div className="cps__ex-t">보증금 자동 계산 예시</div>
                <div className="cps__line"><div><div className="cps__nm">모집 인원</div><div className="cps__sub">인플루언서</div></div><div className="cps__amt">10명</div></div>
                <div className="cps__line"><div><div className="cps__nm">결산 횟수 × 구매당 리워드</div><div className="cps__sub">2회 × 5,000pt</div></div><div className="cps__amt">× 10</div></div>
                <div className="cps__line"><div><div className="cps__nm">최소 보증금</div><div className="cps__sub">10 × 2 × 5,000 × 10</div></div><div className="cps__amt">1,000,000<span className="plus">pt</span></div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 안심 환불 */}
      <section className="block">
        <div className="wrap">
          <div className="reveal" style={{ background: 'var(--mint-soft)', border: '1px solid #cdeee0', borderRadius: '28px', padding: 'clamp(34px,5vw,52px) clamp(22px,4vw,44px)', textAlign: 'center' }}>
            <div style={{ display: 'inline-block', fontWeight: 700, fontSize: '14px', color: '#0a8a5e', background: '#fff', padding: '8px 16px', borderRadius: '999px', marginBottom: '18px', boxShadow: '0 6px 16px -12px rgba(15,179,122,.6)' }}>✅ 안심 환불</div>
            <h2 className="h2" style={{ margin: '0 0 14px' }}>실제 진행된 만큼만<br />비용이 집행됩니다</h2>
            <p className="lead" style={{ maxWidth: '54ch', margin: '0 auto' }}>나머지는 전부 환불 가능해요. 인플루언서 모집이 되지 않거나 결과물이 제출되지 않으면, 비용은 당연히 전부 환불됩니다.</p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="block" id="how" style={{ background: 'var(--bg-soft)', borderRadius: '40px' }}>
        <div className="wrap">
          <div className="reveal" style={{ textAlign: 'center' }}>
            <div className="kicker kicker--blue" style={{ textAlign: 'center' }}>간단한 3단계</div>
            <h2 className="h2">첫 프로젝트, 이렇게 시작합니다</h2>
            <p className="lead" style={{ margin: '0 auto' }}>가입부터 정산까지, 대행사 없이 직접.</p>
          </div>
          <div className="steps">
            <div className="step reveal" data-d="1"><span className="step__ic"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3h18v14H3zM3 9h18M8 21h8"/></svg></span><span className="step__n">STEP 01</span><div className="step__t">프로젝트 등록</div><p className="step__d">예산·인원·채널·기간을 정해 프로젝트를 게시합니다.</p></div>
            <div className="step reveal" data-d="2"><span className="step__ic"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8"/></svg></span><span className="step__n">STEP 02</span><div className="step__t">인플루언서 매칭</div><p className="step__d">지원자 프로필을 보고 프로젝트에 맞는 인플루언서를 선택합니다.</p></div>
            <div className="step reveal" data-d="3"><span className="step__ic"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></span><span className="step__n">STEP 03</span><div className="step__t">검수 & 정산</div><p className="step__d">결과물을 검수해 승인하면 보상이 지급되고, 결산 리포트로 마무리됩니다.</p></div>
          </div>
        </div>
      </section>

      {/* AGENCY */}
      <section className="block agency" id="agency">
        <div className="wrap">
          <div className="reveal">
            <div className="kicker" style={{ color: 'var(--ink)' }}>셀프애드 광고대행</div>
            <h2 className="h2">더 큰 캠페인이 필요하다면,<br />전문가가 함께합니다</h2>
            <p className="lead">셀프로 운영이 부담스럽거나 대규모 캠페인을 준비 중이라면, 셀프애드 전담팀이 기획부터 성과 분석까지 함께합니다.</p>
          </div>
          <div className="ag-grid">
            <div className="ag-card reveal" data-d="1"><div className="ag-card__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.66 18H14.34M11 22h2M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1h6c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2"/></svg></div><div className="ag-card__t">전략 기획</div><p className="ag-card__d">브랜드 목표와 타깃에 맞춘 캠페인 전략을 설계합니다.</p></div>
            <div className="ag-card reveal" data-d="2"><div className="ag-card__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg></div><div className="ag-card__t">인플루언서 매칭</div><p className="ag-card__d">검증된 인플루언서 풀에서 캠페인에 어울리는 크리에이터를 선별·매칭합니다.</p></div>
            <div className="ag-card reveal" data-d="3"><div className="ag-card__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18M7 14l4-4 3 3 5-6"/></svg></div><div className="ag-card__t">성과 분석</div><p className="ag-card__d">집행 결과를 데이터로 정리해 다음 캠페인까지 이어지는 인사이트를 제공합니다.</p></div>
          </div>
          <div className="reveal" style={{ marginTop: '40px' }}><button className="btn btn--lg btn--dark" onClick={() => navigate('/contact')}>광고대행 문의하기 (Agency Inquiry)</button></div>
        </div>
      </section>

      {/* FAQ TEASER */}
      <section className="block" id="faq" style={{ paddingTop: '0' }}>
        <div className="wrap">
          <div className="faqt reveal">
            <div><h3>비용·포인트·정산이 궁금하신가요?</h3><p>광고주가 자주 묻는 질문을 모두 정리했어요.</p></div>
            <button className="btn btn--lg btn--ghost" style={{ background: '#fff' }} onClick={() => navigate('/faq')}>FAQ 보러가기 (View FAQ)</button>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="cta">
        <div className="wrap reveal">
          <div className="kicker kicker--blue" style={{ textAlign: 'center' }}>지금 시작하세요</div>
          <h2 className="h2">첫 프로젝트를<br />직접 만들어 보세요</h2>
          <p className="lead">가입은 무료입니다. 카드 등록 없이 지금 바로 둘러볼 수 있어요.</p>
          <div className="cta__btns"><button className="btn btn--lg btn--primary" onClick={() => navigate(startTo)}>광고주로 시작하기 (Start as Advertiser)</button></div>
          <p className="cta__switch">인플루언서이신가요? <a href="#" role="button" style={{ cursor: 'pointer' }} onClick={(e) => { e.preventDefault(); navigate('/lp/influencer'); }}>인플루언서 페이지 (Influencer page) →</a></p>
        </div>
      </section>

      <footer>
        <div className="wrap">
          <div className="foot__top">
            <div className="brand"><span className="brand__dot"></span>셀프애드</div>
            <nav className="foot__links"><a href="#" onClick={(e) => { e.preventDefault(); navigate('/guide'); }}>서비스 가이드</a><a href="#" onClick={(e) => { e.preventDefault(); navigate('/notice'); }}>공지사항</a><a href="#" onClick={(e) => { e.preventDefault(); navigate('/faq'); }}>FAQ</a><a href="#" onClick={(e) => { e.preventDefault(); navigate('/terms'); }}>이용약관</a><a href="#" onClick={(e) => { e.preventDefault(); navigate('/privacy-policy'); }}>개인정보처리방침</a><a href="#" onClick={(e) => { e.preventDefault(); navigate('/refund-policy'); }}>환불정책</a></nav>
          </div>
          <div className="foot__info"><b>주식회사 카보 (CABO Corp.)</b><br />대표 이동진 · 사업자등록번호 291-86-02840<br />충청남도 계룡시 장안1길 6, 202호 · 고객센터 044-715-7442 · www.selfad.co.kr<br /><span style={{ display: 'inline-block', marginTop: '12px' }}>© 2026 CABO Corp. All rights reserved.</span></div>
        </div>
      </footer>

      {loginOpen && <LoginModal onClose={() => setLoginOpen(false)} />}
    </div>
  );
}

export default MarketerLandingPage;
